import React, { useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useFormik } from "formik";
import { membershipSchema, membershipDefaults } from "../../../../models/membership";
import { MEMBERSHIP_TYPES, DURATION_TYPES } from "../constants";
import PropTypes from "prop-types";
import { OverlayLoader, NeoCard, NeoInput, NeoButton, NeoBadge } from "../../../../components/Common";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";

/**
 * MembershipEditor
 * Formulário central para criação/edição de Planos e Memberships (Membership).
 * Migrado para o Neo Design System.
 */
const MembershipEditor = ({ membership, onSave, onDelete, isSaving, onChange }) => {

    const formik = useFormik({
        initialValues: membershipDefaults,
        validationSchema: membershipSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    useEffect(() => {
        if (membership) {
            formik.resetForm({
                values: {
                    ...membershipDefaults,
                    ...membership,
                    address: {
                        ...membershipDefaults.address,
                        ...(membership.address || {})
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membership]);

    // Notifica o pai quando o form é editado
    useEffect(() => {
        onChange(formik.dirty);
    }, [formik.dirty, onChange]);

    if (!membership) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-file-document-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um plano</h5>
                    <p className="text-muted">Escolha um plano na lista ao lado ou crie um novo para gerenciar planos e mensalidades.</p>
                </div>
            </div>
        );
    }

    return (
        <NeoCard
            title={
                <span className="d-flex align-items-center gap-2">
                    <span className="text-truncate">{formik.values.id ? formik.values.name : "Novo Plano / Membership"}</span>
                    {formik.values.id && (
                        <NeoBadge color={formik.values.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'}>
                            {GLOBAL_STATUS_LABELS[formik.values.status] || 'Desconhecido'}
                        </NeoBadge>
                    )}
                </span>
            }
            subtitle="Gerencie o modelo base deste plano"
            headerIcon="mdi-file-document-multiple-outline"
            headerActions={
                <div className="d-flex gap-2 flex-shrink-0">
                    {formik.values.id && (
                        <NeoButton
                            variant="outline-danger"
                            square={true}
                            onClick={() => onDelete(formik.values.id)}
                            disabled={isSaving}
                            title="Excluir Plano"
                            icon="mdi-trash-can-outline"
                        />
                    )}
                    <NeoButton
                        variant="primary"
                        onClick={formik.handleSubmit}
                        loading={isSaving}
                        loadingText="Salvando..."
                    >
                        Salvar
                    </NeoButton>
                </div>
            }
            className="overflow-hidden rounded-0 position-relative animate__animated animate__fadeIn"
            bodyClassName="p-4"
            noBorder={true}
        >
            <OverlayLoader show={isSaving} label="Salvando..." />
            
            <h6 className="text-uppercase font-size-12 fw-bold mb-4 d-flex align-items-center">
                <i className="mdi mdi-information-outline me-2 font-size-18 text-primary"></i>
                Detalhes do Plano
            </h6>

            <Row className="g-4 mb-5">
                <Col md="8">
                    <NeoInput
                        label="Nome do Plano / Título *"
                        name="name"
                        type="text"
                        flat={true}
                        placeholder="Ex: Mensal Básico, Anual Premium..."
                        error={formik.touched.name && formik.errors.name ? formik.errors.name : ""}
                        {...formik.getFieldProps("name")}
                    />
                </Col>

                <Col md="4">
                    <NeoInput
                        label="Valor Base (R$)"
                        name="value"
                        type="number"
                        flat={true}
                        placeholder="0.00"
                        step="0.01"
                        error={formik.touched.value && formik.errors.value ? formik.errors.value : ""}
                        {...formik.getFieldProps("value")}
                    />
                </Col>

                <Col md="4">
                    <NeoInput
                        label="Modalidade"
                        name="type"
                        type="select"
                        flat={true}
                        {...formik.getFieldProps("type")}
                        onChange={(e) => {
                            formik.setFieldValue("type", e.target.value);
                            // Auto-configurar para pacote de aulas
                            if (e.target.value === 'pass') {
                                formik.setFieldValue("durationType", "classes");
                            }
                            onChange(true);
                        }}
                    >
                        {MEMBERSHIP_TYPES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </NeoInput>
                    {formik.values.type === 'pass' && (
                        <div className="text-info font-size-11 mt-1 d-flex align-items-center">
                            <i className="mdi mdi-information-outline me-1"></i>
                            Pacote: o aluno consome aulas até acabar. Validade padrão de 6 meses.
                        </div>
                    )}
                </Col>

                <Col md="4">
                    <div className="neo-input-wrapper neo-input-wrapper-md">
                        <label className="neo-input-label">
                            {formik.values.durationType === 'classes' ? 'Quantidade de Aulas' : 'Duração & Período'}
                        </label>
                        <div className="d-flex gap-2">
                            <input
                                name="duration"
                                type="number"
                                className={`form-control bg-light bg-opacity-30 border-light ${formik.touched.duration && formik.errors.duration ? 'is-invalid' : ''}`}
                                style={{ width: formik.values.type === 'pass' ? '50%' : '40%' }}
                                placeholder={formik.values.durationType === 'classes' ? 'Ex: 10' : '1'}
                                {...formik.getFieldProps("duration")}
                            />
                            {formik.values.type === 'pass' ? (
                                <div className="d-flex align-items-center bg-light border rounded px-3 flex-grow-1" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                    <i className="mdi mdi-ticket-outline me-1 text-primary"></i>
                                    <span className="font-size-12 fw-medium text-dark">Aulas</span>
                                </div>
                            ) : (
                                <select
                                    className="form-select bg-light bg-opacity-30 border-light flex-grow-1"
                                    {...formik.getFieldProps("durationType")}
                                >
                                    {DURATION_TYPES.filter(opt => opt.value !== 'classes').map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {formik.touched.duration && formik.errors.duration && <div className="text-danger small mt-1">{formik.errors.duration}</div>}
                    </div>
                </Col>

                {formik.values.type === 'pass' && (
                    <Col md="4">
                        <div className="neo-input-wrapper neo-input-wrapper-md">
                            <label className="neo-input-label">Validade do Pacote</label>
                            <div className="input-group">
                                <input
                                    name="classPackageValidityMonths"
                                    type="number"
                                    min="1"
                                    className="form-control bg-light bg-opacity-30 border-light"
                                    placeholder="6"
                                    {...formik.getFieldProps("classPackageValidityMonths")}
                                />
                                <span className="input-group-text bg-light text-dark" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>Meses</span>
                            </div>
                            <span className="text-muted font-size-11">Tempo que o aluno tem para usar as aulas</span>
                        </div>
                    </Col>
                )}

                <Col md="4">
                    <NeoInput
                        label="Máx. Parcelamentos"
                        name="maxInstallments"
                        type="number"
                        flat={true}
                        error={formik.touched.maxInstallments && formik.errors.maxInstallments ? formik.errors.maxInstallments : ""}
                        {...formik.getFieldProps("maxInstallments")}
                    />
                </Col>

                <Col md="12">
                    <NeoInput
                        label="Descrição do Plano (Pública)"
                        name="description"
                        type="textarea"
                        rows="2"
                        flat={true}
                        placeholder="Breve resumo dos benefícios que o aluno adquire ao comprar..."
                        {...formik.getFieldProps("description")}
                    />
                </Col>
            </Row>

            <Row className="g-5">
                {/* REGRAS DE ACESSO */}
                <Col lg="12">
                    <h6 className="text-uppercase font-size-12 fw-bold border-bottom pb-2 mb-3" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                        <i className="mdi mdi-ticket-account me-2 font-size-16 text-primary"></i>Regras de Acesso e Grade
                    </h6>
                    <Row className="g-4">
                        <Col md="4">
                            <div className="d-flex flex-column h-100 p-3 rounded-2 bg-light border" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label className="mb-0 fw-bold font-size-13 cursor-pointer" htmlFor="switch-enroll">Exigir Matrícula</label>
                                    <div className="form-check form-switch form-switch-md mb-0">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            id="switch-enroll"
                                            checked={formik.values.enrollmentRequired === true}
                                            onChange={(e) => {
                                                formik.setFieldValue("enrollmentRequired", e.target.checked);
                                                onChange(true);
                                            }}
                                            className="form-check-input cursor-pointer"
                                        />
                                    </div>
                                </div>
                                <span className="text-muted font-size-11 mb-2">Aluno precisa de tíquetes fixos na grade</span>
                            </div>
                        </Col>
                        
                        <Col md="8">
                            <div className="p-3 border rounded-2" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                <Row className="g-3">
                                    <Col sm="5">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Cota Semanal de Matrículas / Acessos</label>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    type="number"
                                                    name="maxSessionsPerWeek"
                                                    min={0}
                                                    className="form-control bg-light bg-opacity-30 border-light"
                                                    placeholder="0 para Ilimitado"
                                                    {...formik.getFieldProps("maxSessionsPerWeek")}
                                                />
                                                <span className="input-group-text bg-light text-dark" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>Vezes / Sem</span>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="7">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Dias Permitidos na Grade (Se vazio = Livre)</label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {[
                                                    { value: 0, label: "Dom" },
                                                    { value: 1, label: "Seg" },
                                                    { value: 2, label: "Ter" },
                                                    { value: 3, label: "Qua" },
                                                    { value: 4, label: "Qui" },
                                                    { value: 5, label: "Sex" },
                                                    { value: 6, label: "Sáb" }
                                                ].map(day => (
                                                    <div key={day.value} className="form-check form-check-inline me-0 bg-light border rounded px-2 py-1 d-flex align-items-center gap-1" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                                        <input 
                                                            type="checkbox"
                                                            id={`day-${day.value}`}
                                                            className="form-check-input m-0 cursor-pointer"
                                                            checked={formik.values.allowedDaysOfWeek?.includes(day.value)}
                                                            onChange={(e) => {
                                                                let curr = [...(formik.values.allowedDaysOfWeek || [])];
                                                                if (e.target.checked) curr.push(day.value);
                                                                else curr = curr.filter(d => d !== day.value);
                                                                formik.setFieldValue("allowedDaysOfWeek", curr);
                                                                onChange(true);
                                                            }}
                                                        />
                                                        <label className="form-check-label font-size-12 mb-0 fw-medium text-dark cursor-pointer" style={{ paddingTop: "1px" }} htmlFor={`day-${day.value}`}>{day.label}</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Col>

                {/* CAMADAS INFERIORES */}
                <Col lg="12">
                    <Row className="g-4">
                        {/* GAMIFICAÇÃO */}
                        <Col lg="4" md="12">
                            <div className="p-3 border rounded-2 h-100 bg-light" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                <h6 className="text-uppercase font-size-12 fw-bold border-bottom pb-2 mb-3" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                    <i className="mdi mdi-star-shooting-outline me-2 font-size-16 text-primary"></i>Gamificação
                                </h6>
                                <Row className="g-3">
                                    <Col sm="6">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Dá XP ao Aluno (+)</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-info-subtle border-info-subtle text-info"><i className="mdi mdi-star"></i></span>
                                                <input
                                                    name="memberXp" type="number"
                                                    className="form-control bg-white border-light"
                                                    {...formik.getFieldProps("memberXp")}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="6">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Dá XP ao Vendedor (+)</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-success-subtle border-success-subtle text-success"><i className="mdi mdi-account-star"></i></span>
                                                <input
                                                    name="employeeXp" type="number"
                                                    className="form-control bg-white border-light"
                                                    {...formik.getFieldProps("employeeXp")}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        {/* POLÍTICA DE CANCELAMENTO */}
                        <Col lg="4" md="12">
                            <div className="p-3 border rounded-2 h-100 bg-light" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                <h6 className="text-uppercase font-size-12 fw-bold border-bottom pb-2 mb-3 d-flex align-items-center" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                    <i className="mdi mdi-cancel me-2 font-size-16 text-primary"></i>
                                    Política de Cancelamento
                                </h6>
                                <Row className="g-3">
                                    <Col sm="6">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Aviso Prévio Mínimo</label>
                                            <div className="input-group input-group-sm">
                                                <input
                                                    name="cancellationNoticeDays" type="number"
                                                    className="form-control bg-white border-light"
                                                    {...formik.getFieldProps("cancellationNoticeDays")}
                                                />
                                                <span className="input-group-text bg-white text-muted">Dias</span>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm="6">
                                        <div className="neo-input-wrapper neo-input-wrapper-md">
                                            <label className="neo-input-label">Multa Gerada</label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white">%</span>
                                                <input
                                                    name="cancellationFee" type="number"
                                                    className="form-control bg-white border-light"
                                                    {...formik.getFieldProps("cancellationFee")}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>

                        {/* POLÍTICA DE TRANCAMENTO */}
                        <Col lg="4" md="12">
                            <div className="p-3 border rounded-2 h-100 bg-light" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3" style={{ borderColor: "rgba(72, 39, 18, 0.12)" }}>
                                    <h6 className="text-uppercase font-size-12 fw-bold mb-0 d-flex align-items-center">
                                        <i className="mdi mdi-pause-circle-outline me-2 font-size-16 text-primary"></i>
                                        Trancamento
                                    </h6>
                                    <div className="form-check form-switch mb-0">
                                        <input
                                            type="checkbox"
                                            role="switch"
                                            id="switch-suspension"
                                            checked={formik.values.allowsSuspension === true}
                                            onChange={(e) => {
                                                formik.setFieldValue("allowsSuspension", e.target.checked);
                                                onChange(true);
                                            }}
                                            className="form-check-input cursor-pointer"
                                        />
                                    </div>
                                </div>
                                <div style={{ opacity: formik.values.allowsSuspension ? 1 : 0.5, transition: "opacity 0.2s" }}>
                                    <Row className="g-3">
                                        <Col sm="6">
                                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                                <label className="neo-input-label">Limite Global</label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        name="maxSuspensionDays" type="number"
                                                        disabled={!formik.values.allowsSuspension}
                                                        className="form-control bg-white border-light"
                                                        {...formik.getFieldProps("maxSuspensionDays")}
                                                    />
                                                    <span className="input-group-text bg-white text-muted">Dias</span>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col sm="6">
                                            <div className="neo-input-wrapper neo-input-wrapper-md">
                                                <label className="neo-input-label">Taxa (Mensal)</label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-white text-muted">R$</span>
                                                    <input
                                                        name="suspensionFee" type="number"
                                                        disabled={!formik.values.allowsSuspension}
                                                        className="form-control bg-white border-light"
                                                        {...formik.getFieldProps("suspensionFee")}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </NeoCard>
    );
};

MembershipEditor.propTypes = {
    membership: PropTypes.object,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default MembershipEditor;
